import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import EditIcon from "@mui/icons-material/Edit";
import HideSourceIcon from '@mui/icons-material/HideSource';
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, Button, Chip, IconButton, MenuItem, Select, Typography } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { Form, Formik } from "formik";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PaginationButtons from "../../components/Pagination/PaginationButtons";
import { notifySuccess } from "../../components/ToastContainer/Toast/Toast";
import { getReturnInvoiceList, statusUpdate } from "../../service/Invoice/invoice";
import { getOrderDetails } from "../../service/order/orderService";
import AppTheme from "../../theme/AppTheme";
import { dataGridCustomizations, } from "../../theme/customizations";
import ConfirmationDialog from "../Orders/ConfirmationDialog";
import UpdateReturnStatusById from "./UpdateReturnStatusById/UpdateReturnStatusById";
import { styled } from '@mui/material/styles';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import ViewReturnInvoicePopup from "./ViewReturnInvoicePopup";
import GenericFilter from "../../components/GenericFilter/GenericFilter";
import ReturnTableSkeleton from "./Fragments/ReturnTableSkeleton";
import { dispatchResetLoader } from "../../Redux/Utils/loaderDispatcher";
import { notifyError } from "../../components/ToastContainer/Toast/Toast";

const xThemeComponents = {
  ...dataGridCustomizations,

};

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}));

const returnStatusflow = {
  "approved": ["return-picked", "return-pick-failed", "return-delivered"],
  "return-picked": ["return-pick-failed", "return-delivered"],
  "return-pick-failed": ["return-picked", "return-delivered"],
};

const finalStatuses = ["liquidated", "rejected"];

const filterReturnOptions = (currentStatus?: string) => {
  if (!currentStatus || finalStatuses.includes(currentStatus.toLowerCase())) return [];

  const statusList = returnStatusflow[currentStatus] || [];
  return statusList;
};

export default function Return(props: { disableCustomTheme?: boolean }) {
  const [rows, setRows] = useState<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isProcessEnabled, setIsProcessEnabled] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [invoiceId, setInvoiceId] = useState(null)
  const [selectedStatus, setSelectedStatus] = useState<any>({})
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(1);
  const [total, setTotal] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState<any>(null);
  const [flag, setFlag] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | number>(null);
  const navigate = useNavigate();
  const { store_id } = useParams();
  const formikValuesRef = useRef<any>({});
  const submitFormRef = useRef<(() => void) | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [ordersId, setOrdersId] = useState(null);
  const [openViewInvoice, setOpenViewInvoice] = useState(false);

  const [returnStatuses, setReturnStatuses] = useState(() => {
    const sessionData = localStorage.getItem("returnStatus");
    return sessionData ? JSON.parse(sessionData) : {};
  });

  const convertToLocalDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  const mapReturnRows = useCallback((invoices: any) => {
    return invoices?.invoice?.map((invoice: any, index: number) => ({
      index,
      id: invoice.id,
      order_id: invoice.order_id,
      invoice_date: convertToLocalDate(invoice.invoice_date),
      customer_name: invoice.customer_name,
      total_amount: invoice.total_amount.toFixed(2),
      payment_method: invoice.payment_method,
      status: invoice.status || "",
    })) || [];
  }, []);

  const applyReturnResponse = useCallback((returnResponse: any) => {
    const invoices = returnResponse?.data?.data || [];
    setInvoiceId(returnResponse.data.data.id);
    setRows(mapReturnRows(invoices));
    setTotal(returnResponse?.data.data.total);
    setTotalPages(returnResponse?.data.data.totalPages);
    setLimit(returnResponse?.data.data.limit);
  }, [mapReturnRows]);

  const reloadReturnList = useCallback(async () => {
    if (!store_id) {
      return;
    }

    setRows([]);
    setListLoading(true);
    setListError(null);
    dispatchResetLoader();

    try {
      const returnResponse = await getReturnInvoiceList(currentPage, store_id);
      if (returnResponse.status === 200) {
        applyReturnResponse(returnResponse);
      }
    } catch {
      const message = "Failed to load returns. Please try again.";
      setListError(message);
      notifyError("Something went wrong!");
      setInvoiceId(null);
      setRows([]);
    } finally {
      setListLoading(false);
    }
  }, [applyReturnResponse, currentPage, store_id]);

  const fetchReturnInvoiceData = async (filtersData?: any, isFromFilter?: boolean) => {
    if (isFromFilter === false) {
      setFlag(false);
      setFilters(null);
      await reloadReturnList();
      return;
    }

    if (
      filtersData !== undefined &&
      filtersData !== null &&
      Object.keys(filtersData).length > 0
    ) {
      setFilters(filtersData || {});
      setFlag(!!isFromFilter);
      setRows(filtersData?.data || []);
      setListLoading(false);
      setListError(null);
      if (typeof filtersData?.total === "number") setTotal(filtersData.total);
      if (typeof filtersData?.totalPages === "number") {
        setTotalPages(filtersData.totalPages);
      }
      return;
    }

    setFlag(false);
    await reloadReturnList();
  };

  const fetchOrderData = async () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const getApiOrderDetails = await getOrderDetails(currentPage, store_id);
  }

  const resetCallback = () => {
    setFilters(null);
    setFlag(false);
    setRows([]);
  };

  useEffect(() => {
    if (flag || !store_id) {
      return;
    }

    let cancelled = false;

    const loadReturns = async () => {
      setRows([]);
      setListLoading(true);
      setListError(null);
      dispatchResetLoader();

      try {
        const returnResponse = await getReturnInvoiceList(currentPage, store_id);
        if (cancelled) {
          return;
        }
        if (returnResponse.status === 200) {
          applyReturnResponse(returnResponse);
        }
      } catch {
        if (cancelled) {
          return;
        }
        const message = "Failed to load returns. Please try again.";
        setListError(message);
        notifyError("Something went wrong!");
        setInvoiceId(null);
        setRows([]);
      } finally {
        if (!cancelled) {
          setListLoading(false);
        }
      }
    };

    loadReturns();

    return () => {
      cancelled = true;
    };
  }, [applyReturnResponse, currentPage, flag, store_id]);

  const handleReturnStatusChange = async (event, row) => {
    const newStatus = event.target.value;

    const updatedStatuses = {
      ...returnStatuses,
      [row.id]: newStatus,
    };
    localStorage.setItem("returnStatus", JSON.stringify(updatedStatuses));
    setReturnStatuses(updatedStatuses);

    const statusPayload = {
      status: newStatus
    };

    const res = await statusUpdate(row.id, statusPayload);
    if (res.status === 200) {
      notifySuccess("Status Updated");

      setRows((prevRows) =>
        prevRows.map((r) =>
          r.id === row.id ? { ...r, status: newStatus } : r
        )
      );
    }

    fetchReturnInvoiceData();
  };

  const columns: GridColDef[] = useMemo(() => ([
    { field: "invoice_date", headerName: "Date", minWidth: 120, flex: 1 },
    { field: "order_id", headerName: "Order No", minWidth: 100, flex: 1 },
    { field: "id", headerName: "Invoice No", minWidth: 100, flex: 1 },
    { field: "customer_name", headerName: "Customer Name", minWidth: 200, flex: 1 },
    { field: "total_amount", headerName: "Amount", minWidth: 100, flex: 1 },
    { field: "payment_method", headerName: "Payment Method", minWidth: 150, flex: 1 },
    {
      field: "status",
      headerName: "Status",
      minWidth: 222,
      headerAlign: "center",
      flex: 1,
      renderCell: (params) => {
        const statusValue = params.row?.status;
        const currentStatus = statusValue?.toLowerCase();
        // const displayStatus = updatedStatus || currentStatus
        const displayStatus = currentStatus;
        const options = filterReturnOptions(displayStatus);

        const isFinalStatus = displayStatus === "return-delivered" || currentStatus === "liquidated" || currentStatus === "rejected";
        const noDropdownNeeded = options.length === 0 || isFinalStatus;

        const statusLabel =
          currentStatus.charAt(0).toUpperCase() + currentStatus.slice(1).replace(/-/g, " ");

        if (noDropdownNeeded) {
          return (
            <Chip
              label={statusLabel}
              variant="filled"
              color={getStatusStyles[currentStatus] || "default"}
              sx={{
                textTransform: "capitalize",
                fontWeight: 300,
                width: "100%",
                p: 2,
                borderRadius: "2rem",
                maxWidth: "10rem",
                ...getStatusStyles(displayStatus),
              }}
            />
          );
        }

        return (
          <Select
            value={displayStatus}
            onChange={(e) => handleReturnStatusChange(e, params.row)}
            IconComponent={displayStatus === "liquidated" || displayStatus === "rejected" || displayStatus === "created" || displayStatus === "return-delivered" ?
              () => null : ArrowDropDownIcon
            }
            variant="standard"
            sx={{
              width: '100%',
              backgroundColor: "transparent",
              border: "none",
              boxShadow: "none",
            }}
            renderValue={(selected) => (
              <Chip
                label={selected.charAt(0).toUpperCase() + selected.slice(1).replace(/-/g, ' ')}
                variant="filled"
                color={getStatusStyles[selected]}
                sx={{
                  textTransform: "capitalize",
                  fontWeight: 500,
                  width: '100%',
                  maxWidth: "10rem",
                  p: 2,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  "& .MuiChip-label": {
                    fontWeight: 600,
                  },
                  ...getStatusStyles(selected),
                }}
              />
            )}
          >
            {options.map((option) => (
              <MenuItem key={option} value={option}>
                {option.charAt(0).toUpperCase() + option.slice(1).replace(/-/g, ' ')}
              </MenuItem>
            ))}
          </Select>
        );
      }
    },
    {
      field: "action",
      headerName: "Action",
      headerAlign: "center", align: "center",
      minWidth: 100,
      flex: 1,
      renderCell: (params) => {
        return (
          params.row.status === "created" ? (
            <LightTooltip title="Action" placement="right">
              <IconButton
                color="secondary"
                onClick={() => {
                  setSelectedInvoiceId(params.row.id);
                  setDialogOpen(true);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </LightTooltip>
          ) : <LightTooltip title="Disabled" placement="right">
            <IconButton disabled>
              <HideSourceIcon fontSize="small" />
            </IconButton>
          </LightTooltip>
        )
      }
    },
    {
      field: "view",
      headerName: "View",
      headerAlign: "center", align: "center",
      minWidth: 120,
      flex: 1,
      renderCell: (params) => {
        return (
          <LightTooltip title="View" placement="right">
             <IconButton onClick={() => navigate(`/stores/${store_id}/return-invoice/${params.row.id}`)}>
              <VisibilityIcon />
            </IconButton>
          </LightTooltip>
        )
      },
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), []);

  const handlePageChange = (newPage: number) => {
    if (newPage === currentPage) {
      return;
    }

    if (!flag) {
      setRows([]);
      setListLoading(true);
      dispatchResetLoader();
    }

    setCurrentPage(newPage);
  };

  const handleRetryReturns = () => {
    reloadReturnList();
  };

  const showReturnSkeleton = listLoading && !flag;
  const showReturnError =
    Boolean(listError) && rows.length === 0 && !flag && !listLoading;

  const handleProceed = async (values: any) => {
    const invoiceId = values;

    const returnDataList = JSON.parse(localStorage.getItem("returnStatus")) || null;
    const returnStatusData = {
      ...returnDataList,
      [invoiceId]: selectedStatus
    }

    const statusPayload = {
      status: selectedStatus
    }

    const res = await statusUpdate(invoiceId, statusPayload)
    if (res.status === 200) {
      notifySuccess("Status Updated")
    }
    fetchReturnInvoiceData()
    fetchOrderData()
    setSelectedStatus({})
    localStorage.setItem("returnStatus", JSON.stringify(returnStatusData));
    setIsProcessEnabled(true);
  };

  const CustomPagination = () => (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        py: 1,
        px: 1,
      }}
    >
      <PaginationButtons
        page={currentPage}
        count={totalPages}
        totalItems={total}
        itemsPerPage={limit}
        onChange={handlePageChange}
      />
    </Box>
  );

  const handleStatusChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedStatus(event.target.value);
  };

  const getStatusStyles = (status: string) => {
    const cancelledStatuses = ["liquidated", "rejected", "return-pick-failed"];
    const deliveredStatuses = ["return-delivered"];

    if (cancelledStatuses.includes(status)) {
      return {
        backgroundColor: "rgba(243, 153, 148, 0.30)",
        color: "#b71c1c !important",
        border: "1px solid rgba(235, 43, 43, 0.60)"
      };
    } else if (deliveredStatuses.includes(status)) {
      return {
        backgroundColor: "#f0fff4",
        color: "#087f23",
        border: "1px solid #6ee7b7"
      };
    } else {
      return {
        backgroundColor: "rgba(120, 198, 250, 0.30)",
        color: "#fff",
        border: "1px solid rgb(0, 135, 225)"
      };
    }
  };

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{mb:2}}>
        <GenericFilter 
          entity="return" 
          handleGetFetchData={fetchReturnInvoiceData} 
          store_id={store_id} 
          page={currentPage} 
          limit={limit}
          resetCallBack={resetCallback}
        />
      </Box>
      <Box sx={{ width: "100%", maxWidth: { sm: "100%", md: "1700px" }, mx: "auto", height: 520 }}>
        {showReturnSkeleton ? (
          <ReturnTableSkeleton />
        ) : showReturnError ? (
          <Box
            sx={{
              py: 6,
              px: 2,
              textAlign: "center",
              bgcolor: "#fff",
              borderRadius: 1,
              border: "1px solid #f0f0f0",
            }}
          >
            <Typography color="error" variant="body1" sx={{ mb: 2 }}>
              {listError}
            </Typography>
            <Button variant="outlined" onClick={handleRetryReturns}>
              Retry
            </Button>
          </Box>
        ) : (
        <DataGrid
          rows={rows}
          columns={columns}
          autosizeOnMount
          paginationMode="server"
          getRowId={(row) => row.id}
          disableColumnMenu
          loading={false}
          slots={{ pagination: CustomPagination }}
          localeText={{
            noRowsLabel: "No Return Orders",
          }}
        />
        )}

        <ConfirmationDialog
          open={dialogOpen}
          title="Return Details"
          content={
            <Formik
              initialValues={{}}
              onSubmit={() => { }}
            >
              {({ values, submitForm }) => {
                submitFormRef.current = submitForm;
                formikValuesRef.current = values;

                return (
                  <Form>
                    <Box sx={{ height: 300, width: 850 }}>
                      <UpdateReturnStatusById
                        invoiceId={selectedInvoiceId}
                        selectReturnState={selectedStatus}
                        handleStatus={handleStatusChange}
                        fetchData={fetchReturnInvoiceData}
                        onConfirmStatusChange={(newStatus) => {
                          setRows((prev) =>
                            prev.map((row) =>
                              row.id === selectedInvoiceId ? { ...row, status: newStatus } : row
                            )
                          );
                          setDialogOpen(false);
                        }}
                      />
                    </Box>
                  </Form>
                );
              }}
            </Formik>
          }
          onClose={() => setDialogOpen(false)}
          onConfirm={() => {
            handleProceed(selectedInvoiceId)
            setDialogOpen(false);
          }}
          confirmText="Proceed"
        />
      </Box>

      {openViewInvoice && (
        <ViewReturnInvoicePopup open={openViewInvoice} close={() => setOpenViewInvoice(false)} returnId={ordersId} />
      )}

    </AppTheme>
  );
}
